"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import { Link, useNavigate, useLocation, useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { StarIcon } from "@heroicons/react/24/solid"
import { SlidersHorizontal, MapPin, Navigation, Heart, Search, ArrowLeft } from "lucide-react"
import { ref, onValue, query, orderByChild, set, remove, get } from "../lib/firebase-database"
import { db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { MobileBottomNav } from "../components/MobileBottomNav"
import FilterMenu from "./filter-menu"
import { getCachedLocation } from "../utils/locationUtils"
import { Loader2 } from "lucide-react"
import SEOHead from "./SEOHead"
import { format } from "date-fns"
import { X } from "lucide-react"
import Calendar from "react-calendar"
import "react-calendar/dist/Calendar.css"
import { getOptimizedUrl } from "./OptimizedImage"
import lonavalaDesktop from "../assets/Destinations/LonavalaD .webp"
import lonavalaMobileVideo from "../assets/Destinations/lonavala video.webm"
import karjatDesktop from "../assets/Destinations/KarjatD .webp"
import karjatMobileVideo from "../assets/Destinations/Karjat .webm"
import alibagDesktop from "../assets/Destinations/AlibagD .webp"
import alibagMobileVideo from "../assets/Destinations/Alibag Video.webm"
import igatpuriDesktop from "../assets/Destinations/IgatpuriD .webp"
import igatpuriMobileVideo from "../assets/Destinations/Igatpuri.webm"
import panchganiDesktop from "../assets/Destinations/PanchganiD .webp"
import panchganiMobileVideo from "../assets/Destinations/Panchgani video.webm"
import neralDesktop from "../assets/Destinations/NeralD .webp"
import neralMobileVideo from "../assets/Destinations/neral .webm"

const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIntersecting] = useState(false)
  const [node, setNode] = useState(null)

  useEffect(() => {
    if (!node) return

    const observer = new IntersectionObserver(([entry]) => {
      setIntersecting(entry.isIntersecting)
    }, options)

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [node, options])

  return [setNode, isIntersecting]
}

const LazyImage = ({ src, alt, className }) => {
  const [ref, isIntersecting] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "50px",
  })
  const [isLoaded, setIsLoaded] = useState(false)
  const optimizedSrc = src ? getOptimizedUrl(src, 480, 300, 75) : "/placeholder.svg"

  useEffect(() => {
    setIsLoaded(false)
  }, [src])

  return (
    <div ref={ref} className={`relative ${className}`}>
      {isIntersecting ? (
        <>
          <img
            src={optimizedSrc}
            alt={alt}
            className={`${className} ${isLoaded ? "opacity-100" : "opacity-0"} transition-opacity duration-300`}
            onLoad={() => setIsLoaded(true)}
            loading="lazy"
            decoding="async"
          />
          {!isLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />}
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-[inherit]" />
      )}
    </div>
  )
}

// Calendar SVG Component
const CalendarIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
)

// Fixed Calendar Popup Component
const CalendarPopup = ({ isOpen, onClose, onDateSelect, selectedDate, minDate, title, onConfirm }) => {
  const popupRef = useRef(null)
  const [tempSelectedDate, setTempSelectedDate] = useState(selectedDate)

  useEffect(() => {
    setTempSelectedDate(selectedDate)
  }, [selectedDate])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose()
      }
    }

    if (isOpen) {
      document.body.style.overflow = "hidden"
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleDateClick = (date) => {
    setTempSelectedDate(date)
  }

  const handleConfirm = () => {
    if (tempSelectedDate) {
      onDateSelect(tempSelectedDate)
      onConfirm && onConfirm(tempSelectedDate)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Modal Overlay with Blur */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Calendar Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-">
        <div
          ref={popupRef}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-w-sm w-full"
        >
          <div className="bg-black text-white p-4">
            <h3 className="font-semibold text-lg">{title}</h3>
            <p className="text-gray-300 text-sm">Select your preferred date</p>
          </div>

          <div className="p-4">
            <Calendar
              onChange={handleDateClick}
              value={tempSelectedDate}
              minDate={minDate}
              maxDetail="month"
              showNeighboringMonth={false}
              className="react-calendar-custom border-0"
              tileClassName={({ date, view }) => {
                if (view === "month") {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  date.setHours(0, 0, 0, 0)

                  if (date < today) {
                    return "past-date"
                  }
                  if (date.getTime() === today.getTime()) {
                    return "today"
                  }
                }
                return null
              }}
            />

            <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!tempSelectedDate}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Update the destination images mapping
const destinationImages = {
  lonavala: {
    desktop: lonavalaDesktop,
    mobile: {
      type: "video",
      url: lonavalaMobileVideo,
    }
  },
  karjat: {
    desktop: karjatDesktop,
    mobile: {
      type: "video",
      url: karjatMobileVideo,
    }
  },
  alibag: {
    desktop: alibagDesktop,
    mobile: {
      type: "video",
      url: alibagMobileVideo,
    }
  },
  igatpuri: {
    desktop: igatpuriDesktop,
    mobile: {
      type: "video",
      url: igatpuriMobileVideo,
    }
  },
  panchgani: {
    desktop: panchganiDesktop,
    mobile: {
      type: "video",
      url: panchganiMobileVideo,
    }
  },
  neral: {
    desktop: neralDesktop,
    mobile: {
      type: "video",
      url: neralMobileVideo,
    }
  },

  default: {
    desktop: neralDesktop,
    mobile: {
      type: "video",
      url: neralMobileVideo,
    }
  }
}

const DESTINATION_FAQS = {
  lonavala: [
    { q: 'What is the best time to visit Lonavala?', a: 'Lonavala is great year-round. Monsoon (June–September) offers lush greenery and waterfalls. Winter (November–February) is ideal for pleasant weather.' },
    { q: 'How far is Lonavala from Mumbai?', a: 'Lonavala is approximately 83 km from Mumbai, about 1.5–2 hours by car via the Mumbai-Pune Expressway.' },
    { q: 'Do villas in Lonavala have private pools?', a: 'Yes, most villas listed on VillaKosh in Lonavala come with private swimming pools, along with BBQ, gardens, and indoor games.' },
    { q: 'What is the average cost of renting a villa in Lonavala?', a: 'Villa prices range from ₹5,000 to ₹25,000 per night depending on size, amenities, and season.' },
  ],
  karjat: [
    { q: 'How far is Karjat from Mumbai?', a: 'Karjat is about 62 km from Mumbai, roughly 1.5 hours by road. Well-connected by road and local trains.' },
    { q: 'What activities can you do in Karjat?', a: 'Popular activities include trekking, river rafting, waterfall visits during monsoon, and relaxing in private pool villas.' },
    { q: 'Are Karjat villas suitable for large groups?', a: 'Yes, many farmhouses accommodate 10–30 guests with multiple bedrooms, open lawns, and party-friendly spaces.' },
  ],
  alibag: [
    { q: 'How do I reach Alibag from Mumbai?', a: 'By road (95 km, 2–3 hours) or by ferry from Gateway of India to Mandwa jetty (about 1 hour), followed by a short drive.' },
    { q: 'Are there beach-facing villas in Alibag?', a: 'Yes, VillaKosh lists several villas with beach views, beachfront access, and private pools near popular beaches.' },
    { q: 'What is the best season to visit Alibag?', a: 'October to March offers the best weather. Monsoon brings lush greenery but some beach activities may be limited.' },
  ],
  igatpuri: [
    { q: 'How far is Igatpuri from Mumbai?', a: 'Approximately 120 km from Mumbai, about 2–2.5 hours by road via the Mumbai-Nashik Highway.' },
    { q: 'What makes Igatpuri a good vacation destination?', a: 'Cool temperatures year-round, beautiful Sahyadri mountain views, waterfalls during monsoon, and peaceful surroundings.' },
    { q: 'Are there pool villas available in Igatpuri?', a: 'Yes, several villas feature private pools along with mountain views, gardens, and BBQ areas.' },
  ],
  panchgani: [
    { q: 'How far is Panchgani from Pune?', a: 'About 100 km from Pune (2.5 hours by road) and 250 km from Mumbai (5 hours).' },
    { q: 'What are the top attractions near Panchgani?', a: 'Table Land, Sydney Point, Parsi Point, Mapro Garden, and numerous trekking trails.' },
    { q: 'Is Panchgani good for a family vacation?', a: 'Yes — pleasant climate, scenic viewpoints, strawberry farms, and peaceful villas make it ideal year-round.' },
  ],
  neral: [
    { q: 'How far is Neral from Mumbai?', a: 'About 75 km from Mumbai, roughly 1.5 hours by road or train. It is the base for visiting Matheran.' },
    { q: 'What is special about villas in Neral?', a: 'Affordable luxury villas surrounded by dense greenery, close to Matheran, with private pools and spacious lawns.' },
    { q: 'Can I visit Matheran from Neral?', a: 'Yes, Matheran is just 8 km away. Reachable by the toy train, by foot, or by car up to the entry gate.' },
  ],
}

function DestinationFAQ({ city }) {
  const faqs = DESTINATION_FAQS[city?.toLowerCase()] || []
  if (faqs.length === 0) return null

  return (
    <div className="-mx-3 md:-mx-6 lg:-mx-8 mt-2 border-t border-gray-100 px-4 md:px-6 lg:px-8 py-6 pb-28 md:pb-6">
      <h2 className="text-base md:text-xl font-bold text-gray-900 mb-4 text-center">
        FAQs — {city}
      </h2>
      <div className="divide-y divide-gray-100 max-w-2xl mx-auto">
        {faqs.map((faq, idx) => (
          <details key={idx} className="py-3 group">
            <summary className="flex items-center justify-between cursor-pointer text-sm md:text-base font-medium text-gray-900 list-none [&::-webkit-details-marker]:hidden">
              <span className="pr-4">{faq.q}</span>
              <svg
                className="w-4 h-4 shrink-0 text-gray-400 group-open:rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <p className="mt-2 text-sm md:text-[15px] text-gray-600 leading-relaxed">
              {faq.a}
            </p>
          </details>
        ))}
      </div>
    </div>
  )
}

export default function DestinationPage({ cityName }) {
  const { destination } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const currentCity =
    cityName ||
    (destination ? destination.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "") ||
    location.state?.cityName ||
    ""

  const [searchQuery, setSearchQuery] = useState("")
  const [properties, setProperties] = useState([])
  const [allProperties, setAllProperties] = useState([])
  const [filteredProperties, setFilteredProperties] = useState([])
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState("recommended")
  const [showFilters, setShowFilters] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [userCity, setUserCity] = useState("")
  const [userState, setUserState] = useState("")
  const [isFilteringLoading, setIsFilteringLoading] = useState(false)
  const [checkInDate, setCheckInDate] = useState(null)
  const [checkOutDate, setCheckOutDate] = useState(null)
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [selectedCity, setSelectedCity] = useState(currentCity)
  const [cityList, setCityList] = useState([])
  const [isSearchVisible, setIsSearchVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [showBot, setShowBot] = useState(false)
  const [wishlistedProperties, setWishlistedProperties] = useState({})
  const [searchResults, setSearchResults] = useState([])
  const [crossLocationMatches, setCrossLocationMatches] = useState([])
  const [showNoResultsMessage, setShowNoResultsMessage] = useState(false)
  const [activeDateFilter, setActiveDateFilter] = useState(null)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [heroMediaFailed, setHeroMediaFailed] = useState(false)

  // Animation states
  const [showInitialView, setShowInitialView] = useState(true)
  const [isAnimating, setIsAnimating] = useState(false)

  // Scroll effect states
  const [scrollY, setScrollY] = useState(0)

  // Enhanced Calendar popup states
  const [showCheckInCalendar, setShowCheckInCalendar] = useState(false)
  const [showCheckOutCalendar, setShowCheckOutCalendar] = useState(false)

  const { user } = useAuth()

  const getDestinationDesktopImage = useCallback((cityName) => {
    const city = cityName.toLowerCase().replace(/\s+/g, "")
    const images = destinationImages[city] || destinationImages.default
    return images.desktop || destinationImages.default.desktop
  }, [])

  useEffect(() => {
    document.title = `Villas in ${currentCity} - VillaKosh`
  }, [currentCity])

  const destinationTitle = `Villas in ${currentCity} - Book Private Pool Villas | VillaKosh`
  const destinationDescription = `Discover ${currentCity}'s best private villas with pools, scenic views, and verified hosts. Book luxury villa stays in ${currentCity} on VillaKosh.`

  useEffect(() => {
    const cachedData = getCachedLocation()
    if (cachedData) {
      setUserLocation(cachedData.location)
      setUserCity(cachedData.city)
      setUserState(cachedData.state)
    }

    fetchProperties()

    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768
      setIsMobile(newIsMobile)
      setIsMobileView(newIsMobile)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (user) {
      const wishlistRef = ref(db, `wishlists/${user.uid}`)
      onValue(wishlistRef, (snapshot) => setWishlistedProperties(snapshot.val() || {}))
    }
  }, [user])

  useEffect(() => {
    if (allProperties.length > 0) {
      const uniqueCities = Array.from(new Set(allProperties.map((p) => p.mainCity).filter(Boolean)))
      setCityList(uniqueCities)
    }
  }, [allProperties])

  useEffect(() => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    setCheckInDate(today)
    setCheckOutDate(tomorrow)
  }, [])

  // Enhanced scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setScrollY(currentScrollY)
      setIsSearchVisible(currentScrollY < lastScrollY || currentScrollY < 100)
      setLastScrollY(currentScrollY)
    }

    let ticking = false
    const smoothScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("scroll", smoothScrollHandler, { passive: true })
    return () => window.removeEventListener("scroll", smoothScrollHandler)
  }, [lastScrollY])

  const fetchProperties = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const villasRef = ref(db, "villas")
      const villasQuery = query(villasRef, orderByChild("createdAt"))

      onValue(
        villasQuery,
        (snapshot) => {
          const data = snapshot.val()
          const fetchedProperties = data
            ? Object.entries(data)
                .map(([key, value]) => ({
                  _id: key,
                  ...value,
                }))
                .filter((property) => !property.isHidden)
            : []

          const cityFilteredProperties = fetchedProperties.filter(
            (property) => property.mainCity?.toLowerCase() === currentCity.toLowerCase(),
          )

          setProperties(cityFilteredProperties)
          setAllProperties(fetchedProperties)
          setFilteredProperties(cityFilteredProperties)
          setIsLoading(false)
        },
        (error) => {
          console.error("Error fetching properties:", error)
          setError("Failed to load properties. Please try again later.")
          setIsLoading(false)
        },
      )
    } catch (error) {
      console.error("Error:", error)
      setError("An unexpected error occurred")
      setIsLoading(false)
    }
  }

  const filterProperties = useCallback(() => {
    if (searchQuery.length >= 3) {
      setShowNoResultsMessage(false)
      setCrossLocationMatches([])

      const searchTerms = searchQuery.toLowerCase().split(" ")

      const currentLocationMatches = properties.filter((property) => {
        const propertyText = `${property.name || ""} ${property.address || ""} ${(property.facilities || []).join(
          " ",
        )}`.toLowerCase()
        return searchTerms.every((term) => propertyText.includes(term))
      })

      const otherLocationMatches = allProperties.filter((property) => {
        if (property.mainCity?.toLowerCase() === currentCity.toLowerCase()) return false

        const propertyText = `${property.name || ""} ${property.address || ""} ${(property.facilities || []).join(
          " ",
        )}`.toLowerCase()
        return searchTerms.every((term) => propertyText.includes(term))
      })

      setFilteredProperties(currentLocationMatches)
      setCrossLocationMatches(otherLocationMatches)
      setShowNoResultsMessage(currentLocationMatches.length === 0)
    } else {
      setFilteredProperties(properties)
      setCrossLocationMatches([])
      setShowNoResultsMessage(false)
    }
  }, [searchQuery, properties, allProperties, currentCity])

  useEffect(() => {
    filterProperties()
  }, [filterProperties])

  // Fixed date selection handlers
  const handleCheckInSelect = (date) => {
    setCheckInDate(date)
    setShowCheckInCalendar(false)

    // Auto-open checkout calendar if checkout date is before or same as checkin
    if (!checkOutDate || checkOutDate <= date) {
      const nextDay = new Date(date)
      nextDay.setDate(nextDay.getDate() + 1)
      setCheckOutDate(nextDay)
      setTimeout(() => {
        setShowCheckOutCalendar(true)
      }, 300)
    }
  }

  const handleCheckOutSelect = (date) => {
    setCheckOutDate(date)
    setShowCheckOutCalendar(false)
  }

  const handleSort = (value) => {
    setSortBy(value)
    const sorted = [...filteredProperties]
    switch (value) {
      case "recommended":
        sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
        break
      case "price-low-high":
        sorted.sort((a, b) => (a.rate || 0) - (b.rate || 0))
        break
      case "price-high-low":
        sorted.sort((a, b) => (b.rate || 0) - (a.rate || 0))
        break
      default:
        break
    }
    setFilteredProperties(sorted)
  }

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371
    const dLat = deg2rad(lat2 - lat1)
    const dLon = deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const deg2rad = (deg) => {
    return deg * (Math.PI / 180)
  }

  const applyFilters = async (filters) => {
    setIsFilteringLoading(true)

    try {
      await new Promise((resolve) => setTimeout(resolve, 500))

      let filtered = [...properties]

      if (filters.priceRange) {
        filtered = filtered.filter(
          (property) => property.rate >= filters.priceRange[0] && property.rate <= filters.priceRange[1],
        )
      }

      if (filters.rating > 0) {
        filtered = filtered.filter((property) => (property.averageRating || 0) >= filters.rating)
      }

      if (filters.facilities && filters.facilities.length > 0) {
        filtered = filtered.filter((property) =>
          filters.facilities.every((facility) => property.facilities && property.facilities.includes(facility)),
        )
      }

      if (filters.distance && userLocation) {
        filtered = filtered.filter((property) => {
          if (!property.location) return false
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            property.location.lat,
            property.location.lng,
          )
          return distance <= filters.distance
        })
      }

      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "recommended":
            filtered.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
            break
          case "price-low-high":
            filtered.sort((a, b) => (a.rate || 0) - (b.rate || 0))
            break
          case "price-high-low":
            filtered.sort((a, b) => (b.rate || 0) - (a.rate || 0))
            break
          default:
            break
        }
      }

      setFilteredProperties(filtered)
      setShowFilters(false)
    } finally {
      setIsFilteringLoading(false)
    }
  }

  const handleWishlistClick = async (propertyId) => {
    if (!user) {
      navigate("/login")
      return
    }
    const wishlistRef = ref(db, `wishlists/${user.uid}/${propertyId}`)
    const wasWishlisted = isPropertyWishlisted(propertyId)
    setWishlistedProperties((prev) => {
      const next = { ...prev }
      if (wasWishlisted) delete next[propertyId]
      else next[propertyId] = true
      return next
    })
    try {
      if (wasWishlisted) {
        await remove(wishlistRef)
      } else {
        await set(wishlistRef, true)
      }
    } catch (error) {
      console.error("Wishlist update failed:", error)
      setWishlistedProperties((prev) => {
        const next = { ...prev }
        if (wasWishlisted) next[propertyId] = true
        else delete next[propertyId]
        return next
      })
      alert("Failed to update favorites. Please try again.")
    }
  }

  const isPropertyWishlisted = (propertyId) => !!wishlistedProperties[propertyId]

  const handleDateAvailabilityFilter = async () => {
    if (!checkInDate || !checkOutDate) return

    const startDate = new Date(checkInDate)
    const endDate = new Date(checkOutDate)

    setIsLoading(true)
    const availableVillas = []

    for (const property of properties) {
      const bookingsRef = ref(db, `bookings/${property._id}`)
      const snapshot = await get(bookingsRef)
      let isAvailable = true

      if (snapshot.exists()) {
        const bookings = Object.values(snapshot.val())
        for (const booking of bookings) {
          const bookedStart = new Date(booking.startDate)
          const bookedEnd = new Date(booking.endDate)
          if (startDate <= bookedEnd && endDate >= bookedStart) {
            isAvailable = false
            break
          }
        }
      }

      if (isAvailable) {
        availableVillas.push(property)
      }
    }

    setFilteredProperties(availableVillas)
    setActiveDateFilter({
      checkIn: checkInDate,
      checkOut: checkOutDate,
    })
    setIsLoading(false)
    handleMinimizeSearch()
  }

  const clearDateFilter = () => {
    setActiveDateFilter(null)
    setFilteredProperties(properties)
  }

  const handleCitySelect = (city) => {
    setSelectedCity(city)
    setShowCityDropdown(false)
    setSearchQuery(city)
    navigate(`/villas-in-${city.toLowerCase().replace(/\s+/g, "-")}`)
  }

  const handleSelectDatesClick = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setShowInitialView(false)
      setTimeout(() => {
        setIsAnimating(false)
      }, 100)
    }, 400)
  }

  const handleMinimizeSearch = () => {
    setIsAnimating(true)
    setTimeout(() => {
      setShowInitialView(true)
      setTimeout(() => {
        setIsAnimating(false)
      }, 100)
    }, 400)
  }

  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && event.target.type !== "text") {
        setShowCityDropdown(false)
      }
    }
    if (showCityDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showCityDropdown])

  const PropertySkeleton = () => (
  <div className="flex-shrink-0 w-full bg-gray-50 rounded-[20px] overflow-hidden border-2 shadow border-gray-200 animate-pulse">
    <div className="relative p-2">
      <div className="rounded-[10px] overflow-hidden">
        <div className="grid grid-cols-2 gap-1 h-40">
          <div className="relative h-40 bg-gray-200 rounded-l-[20px]" />
          <div className="grid grid-rows-2 gap-2 h-40">
            <div className="h-[5rem] bg-gray-200 rounded-tr-[15px]" />
            <div className="h-[5rem] bg-gray-200 rounded-br-[15px]" />
          </div>
        </div>
      </div>
    </div>
    <div className="px-3 pb-2">
      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded-full" />
          <div className="ml-1 h-4 bg-gray-200 rounded w-1/3" />
        </div>
        <div className="flex items-center">
          <div className="h-3.5 w-3.5 bg-gray-200 rounded-full" />
          <div className="ml-1 h-4 bg-gray-200 rounded w-12" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="h-5 bg-gray-200 rounded w-1/4" />
        <div className="h-4 bg-gray-200 rounded w-16" />
      </div>
    </div>
  </div>
)

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      <SEOHead destination={currentCity} />

      {/* Fixed Hero Section with Reduced Height and Visible Curve */}
      <div
        className="sticky top-0 overflow-hidden z-10 hero-section transition-all duration-300 ease-out"
        style={{
          height: `${Math.max(400, 500 - scrollY * 0.3)}px`, // Reduced from 600px to 400px
        }}
      >
        {/* Background Image/Video */}
        <div className="absolute inset-0">
          {isMobileView && !heroMediaFailed ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                poster={getDestinationDesktopImage(currentCity)}
                onError={() => setHeroMediaFailed(true)}
              >
                <source
                  src={destinationImages[currentCity.toLowerCase().replace(/\s+/g, "")]?.mobile?.url || destinationImages.default.mobile?.url}
                  type="video/webm"
                />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={getDestinationDesktopImage(currentCity)}
                alt={`${currentCity} destination`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const fallback = getDestinationDesktopImage("default")
                  if (e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback
                  }
                }}
              />
            )}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>

        {/* Always Visible Curved Bottom Design */}
        <div className="absolute bottom-0 left-0 right-0 bg-white">
          <svg
            className="absolute top-0 left-0 w-full h-8"
            viewBox="0 0 1200 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path d="M0 32V16C200 0 400 0 600 16C800 32 1000 32 1200 16V32H0Z" fill="white" />
          </svg>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-4 z-20 p-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-full text-white hover:bg-opacity-30 transition-all"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4">
          {/* Initial View */}
          {showInitialView && (
            <div
              className={`transition-all duration-500 ease-in-out transform ${
                isAnimating ? "-translate-y-20 opacity-0 scale-95" : "translate-y-0 opacity-100 scale-100"
              }`}
            >
              <h1 className="text-[46px] md:text-6xl font-bold text-white mb-2 drop-shadow-2xl">{currentCity}</h1>
              <p className="text-lg md:text-xl text-white mb-8 drop-shadow-lg">
                {filteredProperties.length} Properties Available
              </p>

              <div className="flex flex-col items-center mt-4 py-4">
                <button
                  onClick={handleSelectDatesClick}
                  className="w-16 h-16 bg-white bg-opacity-90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl hover:bg-opacity-100 transition-all duration-300 hover:scale-110 group transform"
                  disabled={isAnimating}
                >
                  <CalendarIcon className="h-6 w-6 text-black group-hover:text-black transition-colors" />
                </button>
                <p className="text-white text-md font-medium mt-4 drop-shadow-lg">Select Dates</p>
              </div>
            </div>
          )}

          {/* Expanded Search Form */}
          {!showInitialView && (
            <div
              className={`w-full transition-all duration-500 ease-in-out transform ${
                isAnimating ? "translate-y-20 opacity-0 scale-95" : "translate-y-0 opacity-100 scale-100"
              }`}
            >
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 drop-shadow-2xl">{currentCity}</h1>
              <p className="text-md md:text-lg text-white mb-6 drop-shadow-lg">
                {filteredProperties.length} Properties Available
              </p>

              <div className="w-full max-w-4xl mx-auto px-4">
                <div className="bg-white rounded-[24px] p-3 md:p-6 shadow-2xl">
                  <div className="flex flex-col gap-3 md:grid md:grid-cols-[2fr,1fr,1fr,auto] md:gap-4">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder={`Search villas in ${currentCity}`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 md:py-4 rounded-full bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black text-sm md:text-base transition-all"
                      />
                    </div>

                    {/* Enhanced Date Selection */}
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={checkInDate ? format(checkInDate, "dd MMM") : ""}
                          onClick={() => setShowCheckInCalendar(true)}
                          readOnly
                          className="w-full px-3 py-3 md:py-4 rounded-full bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black transition-all text-sm cursor-pointer"
                          placeholder="Check In"
                        />
                        <label className="absolute -top-2 left-3 bg-white px-2 text-xs text-gray-600 font-medium">
                          Check In
                        </label>
                      </div>

                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={checkOutDate ? format(checkOutDate, "dd MMM") : ""}
                          onClick={() => setShowCheckOutCalendar(true)}
                          readOnly
                          className="w-full px-3 py-3 md:py-4 rounded-full bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-black transition-all text-sm cursor-pointer"
                          placeholder="Check Out"
                        />
                        <label className="absolute -top-2 left-3 bg-white px-2 text-xs text-gray-600 font-medium">
                          Check Out
                        </label>
                      </div>
                    </div>

                    {/* Search Button */}
                    <button
                      className="bg-red-400 hover:bg-red-500 text-white px-6 py-3 md:py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto md:h-fit md:self-center"
                      onClick={handleDateAvailabilityFilter}
                      disabled={!checkInDate || !checkOutDate}
                    >
                      <Search className="h-5 w-5" />
                      <span>Search</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content with Seamless Integration */}
      <main className="container mx-auto -mt-8 relative z-30">
        <div className="bg-white rounded-t-[30px] min-h-[600px] shadow-xl overflow-hidden">
          <div className="p-3 md:p-6 lg:p-8">
            <div className="lg:grid lg:grid-cols-[320px,1fr] lg:gap-6">
              {!isMobile && (
                <FilterMenu
                  isOpen={true}
                  onClose={() => {}}
                  onApply={(filters) => {
                    applyFilters(filters)
                    if (filters.sortBy !== sortBy) {
                      setSortBy(filters.sortBy)
                    }
                  }}
                  sortBy={sortBy}
                  variant="sidebar"
                />
              )}
              <div>
                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[...Array(8)].map((_, index) => (
                      <PropertySkeleton key={index} />
                    ))}
                  </div>
                ) : (
                  <>
                {/* Search Results Section */}
                {searchQuery.length >= 3 && (
                  <div className="mb-6">
                    {showNoResultsMessage && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-gray-600 mb-2">
                          No villas found in {currentCity} matching "{searchQuery}"
                        </p>
                        {crossLocationMatches.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-500 mb-2">
                              However, we found matching villas in other locations:
                            </p>
                            <div className="space-y-2">
                              {crossLocationMatches.map((property) => (
                                <div
                                  key={property._id}
                                  className="flex items-center justify-between bg-white p-3 rounded-lg border"
                                >
                                  <div className="flex items-center gap-3">
                                    <MapPin className="h-5 w-5 text-gray-400" />
                                    <div>
                                      <p className="font-medium">{property.name}</p>
                                      <p className="text-sm text-gray-500">Located in {property.mainCity}</p>
                                    </div>
                                  </div>
                                  <Link
                                    to={`/villa/${property._id}`}
                                    className="text-black font-medium text-sm hover:underline"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      navigate(`/villa/${property._id}`, { replace: true })
                                    }}
                                  >
                                    View Details
                                  </Link>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Villa Grid */}
                {filteredProperties.length === 0 && !searchQuery ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <h2 className="text-xl font-semibold text-gray-600 mb-2">No villas found in {currentCity}</h2>
                    <p className="text-gray-500 text-center mb-4">
                      We couldn't find any villas matching your criteria in {currentCity}.
                    </p>
                    <button
                      onClick={() => navigate("/explore")}
                      className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                    >
                      Explore All Cities
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-4 pb-32">
                    {filteredProperties.map((property) => (
                      <React.Fragment key={property._id}>
                        <Link
                          to={`/villa/${property._id}`}
                          className="flex-shrink-0 w-full bg-white rounded-[20px] overflow-hidden border-2 border-gray-300 hover:shadow-lg transition-shadow"
                        >
                          <div className="relative p-2">
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                handleWishlistClick(property._id)
                              }}
                              className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                            >
                              {isPropertyWishlisted(property._id) ? (
                                <Heart className="h-5 w-5 text-red-500 fill-current" />
                              ) : (
                                <Heart className="h-5 w-5 text-gray-600 hover:text-red-500 transition-colors" />
                              )}
                            </button>
                            <div className="rounded-[20px] overflow-hidden">
                              <div className="grid grid-cols-2 gap-1 h-40">
                                <div className="relative h-40">
                                  <LazyImage
                                    src={property.thumbnail || property.images[0]}
                                    alt={property.name}
                                    className="w-full h-full object-cover rounded-l-[20px]"
                                  />
                                </div>
                                <div className="grid grid-rows-2 gap-2 h-40">
                                  <div className="h-[5rem]">
                                    <LazyImage
                                      src={property.images[1] || "/placeholder.svg"}
                                      alt={property.name}
                                      className="w-full h-full object-cover rounded-tr-[15px]"
                                    />
                                  </div>
                                  <div className="h-[5rem]">
                                    <LazyImage
                                      src={property.images[2] || "/placeholder.svg"}
                                      alt={property.name}
                                      className="w-full h-full object-cover rounded-br-[15px]"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="px-3 pb-2">
                            <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{property.name}</h3>
                            <div className="mt-1 flex items-center justify-between text-sm text-gray-600">
                              <div className="flex items-center">
                                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="ml-1 truncate">{property.mainCity}</span>
                              </div>
                              <div className="flex items-center">
                                <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="ml-1 font-medium">
                                  {property.averageRating ? property.averageRating.toFixed(1) : "0.0"}
                                </span>
                                <span className="ml-1">({property.reviewCount || 0})</span>
                              </div>
                            </div>
                            <div className="mt-1 flex items-center justify-between">
                              <p className="text-base font-bold text-gray-900">
                                ₹{property.rate}
                                {property.pricingType === 'perPerson' && property.rate && (property.perPersonGuestCount || property.guestCount) && (
                                  <span className="text-xs text-gray-600 ml-1">
                                    Est.₹{Math.round(property.rate / (property.perPersonGuestCount || property.guestCount))}/guest
                                  </span>
                                )}
                              </p>
                              <div className="text-sm text-gray-600 truncate">
                                {userLocation && property.location ? (
                                  <span>{Math.round(calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng) * 10) / 10} km</span>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </>
            )}
              </div>
            </div>

            <DestinationFAQ city={currentCity} />
          </div>
        </div>
      </main>

      {/* Floating Filter Button */}
      {isMobile && (
        <button
          onClick={() => setShowFilters(true)}
          className="fixed bottom-20 left-4 z-50 bg-black text-white py-3 px-6 rounded-full shadow-lg flex items-center gap-2 hover:bg-gray-800 transition-all duration-300"
          disabled={isFilteringLoading}
        >
          {isFilteringLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <SlidersHorizontal className="h-5 w-5" />
          )}
          <span>{isFilteringLoading ? "Filtering..." : "Filter"}</span>
        </button>
      )}

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileBottomNav user={user} className="h-16" />}

      {/* Filter Menu */}
      {isMobile && (
        <FilterMenu
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApply={(filters) => {
            applyFilters(filters)
            if (filters.sortBy !== sortBy) {
              setSortBy(filters.sortBy)
            }
          }}
          sortBy={sortBy}
          initialFilters={{
            priceRange: [0, 50000],
            rating: 0,
            facilities: [],
            distance: null,
          }}
        />
      )}

      {/* Date Filter Info */}
      {activeDateFilter && (
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Showing available stays for:</span>
            <div className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center gap-2">
              <span>
                {format(new Date(activeDateFilter.checkIn), "dd MMM")} -{" "}
                {format(new Date(activeDateFilter.checkOut), "dd MMM")}
              </span>
              <button onClick={clearDateFilter} className="hover:bg-gray-200 rounded-full p-1">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Calendar Styles */}
      <style jsx global>{`
        .react-calendar-custom {
          width: 100%;
          border: none !important;
          font-family: inherit;
        }
        
        .react-calendar-custom .react-calendar__navigation {
          margin-bottom: 1rem;
        }
        
        .react-calendar-custom .react-calendar__navigation button {
          background: none;
          border: none;
          color: #374151;
          font-size: 1rem;
          font-weight: 600;
          padding: 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.2s;
        }
        
        .react-calendar-custom .react-calendar__navigation button:hover {
          background-color: #f3f4f6;
        }
        
        .react-calendar-custom .react-calendar__tile {
          background: none;
          border: none;
          padding: 0.75rem 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.2s;
          color: #374151;
          font-weight: 500;
        }
        
        .react-calendar-custom .react-calendar__tile:hover {
          background-color: #f3f4f6;
          color: #000000;
        }
        
        .react-calendar-custom .react-calendar__tile--active {
          background-color: #000000 !important;
          color: white !important;
        }
        
        .react-calendar-custom .react-calendar__tile.today {
          background-color: #fef3c7;
          color: #92400e;
          font-weight: 600;
        }
        
        .react-calendar-custom .react-calendar__tile.past-date {
          color: #d1d5db;
          cursor: not-allowed;
        }
        
        .react-calendar-custom .react-calendar__tile.past-date:hover {
          background: none;
          color: #d1d5db;
        }
        
        .react-calendar-custom .react-calendar__month-view__weekdays {
          text-transform: uppercase;
          font-weight: 600;
          font-size: 0.75rem;
          color: #6b7280;
        }
        
        .react-calendar-custom .react-calendar__month-view__weekdays__weekday {
          padding: 0.5rem;
        }
      `}</style>

      {/* Fixed Calendar Popups */}
      <CalendarPopup
        isOpen={showCheckInCalendar}
        onClose={() => setShowCheckInCalendar(false)}
        onDateSelect={handleCheckInSelect}
        selectedDate={checkInDate}
        minDate={new Date()}
        title="Select Check-in Date"
      />

      <CalendarPopup
        isOpen={showCheckOutCalendar}
        onClose={() => setShowCheckOutCalendar(false)}
        onDateSelect={handleCheckOutSelect}
        selectedDate={checkOutDate}
        minDate={checkInDate ? new Date(checkInDate.getTime() + 24 * 60 * 60 * 1000) : new Date()}
        title="Select Check-out Date"
      />
    </div>
  )
}
